import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type { AuthApiClient } from '@/stores/authStore';
import type { AccessibilityCatalogSection, AgeRating, ApiResponse, DeveloperAnalyticsPeriod, DeveloperGameAnalytics, DeveloperGameTrendAnalytics, GameSystemRequirements } from '@/types';
import { API_BASE_URL } from '@/config/api';
import type { GameTaxonomyCatalogGroup } from '@ludolio/shared-games';

declare global {
  interface Window {
    __isLoggingOut?: boolean;
  }
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const url: string | undefined = error.config?.url;

        const isAuthCheck =
          url?.includes('/auth/logout') ||
          url?.includes('/auth/me');

        const isOtpVerification =
          url?.includes('/auth/verify-otp') ||
          url?.includes('/auth/desktop-verify-otp') ||
          url?.includes('/auth/developer/verify-otp');

        if (status === 401 && !isAuthCheck && !isOtpVerification) {
          const { useAuthStore } = await import('@/stores/authStore');

          if (!window.__isLoggingOut) {
            window.__isLoggingOut = true;
            try {
              await useAuthStore.getState().logout();
            } catch (logoutError) {
              console.error('Logout failed during 401 handling:', logoutError);
            } finally {
              window.__isLoggingOut = false;
            }
            window.location.href = '/';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async sendOtp(email: string) {
    // Use developer-specific endpoint that allows any email
    const response = await this.client.post('/auth/developer/send-otp', { email });
    return response.data;
  }

  async verifyOtp(
    email: string,
    token: string,
    options?: {
      firstName?: string;
      lastName?: string;
      orgUsername?: string;
      studioName?: string;
      website?: string;
    }
  ) {
    const payload: {
      email: string;
      token: string;
      firstName?: string;
      lastName?: string;
      orgUsername?: string;
      studioName?: string;
      website?: string;
    } = { email, token };
    if (options?.firstName) {
      payload.firstName = options.firstName;
    }
    if (options?.lastName) {
      payload.lastName = options.lastName;
    }
    if (options?.orgUsername) {
      payload.orgUsername = options.orgUsername;
    }
    if (options?.studioName) {
      payload.studioName = options.studioName;
    }
    if (options?.website) {
      payload.website = options.website;
    }
    // Use developer-specific endpoint that creates developer accounts
    const response = await this.client.post('/auth/developer/verify-otp', payload);
    return response.data;
  }

  async updateName(firstName: string, lastName: string, orgUsername?: string) {
    const payload: any = { firstName, lastName };
    if (orgUsername !== undefined) {
      payload.orgUsername = orgUsername;
    }
    const response = await this.client.post('/auth/update-name', payload);
    return response.data;
  }

  async getOnboardingInfo() {
    const response = await this.client.post('/auth/onboarding-info');
    return response.data;
  }

  async completeOnboarding(input: { firstName: string; lastName: string; marketingConsent: boolean; privacyAccepted: boolean; orgNoticeId?: string | null }) {
    const response = await this.client.post('/auth/complete-onboarding', input);
    return response.data;
  }

  async acceptOrgNotice(orgNoticeId: string) {
    const response = await this.client.post('/auth/accept-org-notice', { orgNoticeId });
    return response.data;
  }

  async logout() {
    await this.client.post('/auth/logout');
    return { success: true, data: undefined };
  }

  async validateToken() {
    const response = await this.client.post('/auth/validate');
    return response.data;
  }

  async getCurrentUser() {
    const response = await this.client.post('/auth/me');
    return response.data;
  }

  // Developer endpoints
  async getDeveloperProfile() {
    const response = await this.client.post('/developer/profile');
    return response.data;
  }

  async listDeveloperGames() {
    const response = await this.client.post('/developer/games/list');
    return response.data;
  }

  async getDeveloperGamesAnalytics(
    period: DeveloperAnalyticsPeriod
  ): Promise<ApiResponse<DeveloperGameAnalytics[]>> {
    const response = await this.client.post('/developer/games/analytics', { period });
    return response.data;
  }

  async getDeveloperGamesAnalyticsByDateRange(
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<DeveloperGameAnalytics[]>> {
    const response = await this.client.post('/developer/games/analytics-range', { startDate, endDate });
    return response.data;
  }

  async getDeveloperGameTrendAnalytics(
    period: DeveloperAnalyticsPeriod
  ): Promise<ApiResponse<DeveloperGameTrendAnalytics[]>> {
    const response = await this.client.post('/developer/games/analytics-trends', { period });
    return response.data;
  }

  async getDeveloperGameTrendAnalyticsByDateRange(
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<DeveloperGameTrendAnalytics[]>> {
    const response = await this.client.post('/developer/games/analytics-trends-range', { startDate, endDate });
    return response.data;
  }

  async getDeveloperGame(gameId: string) {
    const response = await this.client.post('/developer/games/get', { gameId });
    return response.data;
  }

  async getAccessibilityCatalog(): Promise<ApiResponse<AccessibilityCatalogSection[]>> {
    const response = await this.client.post('/games/accessibility-catalog');
    return response.data;
  }

  async getTaxonomyCatalog(): Promise<ApiResponse<GameTaxonomyCatalogGroup[]>> {
    const response = await this.client.get('/game-taxonomy/catalog');
    return {
      ...response.data,
      data: response.data.data.map((catalogGroup: any) => ({
        group: catalogGroup.group || catalogGroup.id,
        name: catalogGroup.name || catalogGroup.label,
        tags: catalogGroup.tags.map((tag: any) => ({
          ...tag,
          name: tag.name || tag.label,
          slug: tag.slug || createTagSlug(tag.name || tag.label),
          assignmentCount: tag.assignmentCount || 0,
        })),
      })),
    };
  }

  async submitGame(data: {
    title: string;
    description?: string;
    publisher?: string;
    version?: string;
    iconUrl?: string;
    bannerUrl?: string;
    galleryImages?: string[];
    accessibilityFeatureIds?: string[];
    isDrmFree?: boolean;
	    ageRatings?: AgeRating[];
    systemRequirements?: GameSystemRequirements;
    estimatedPlaytime?: string;
    estimatedPlaytimeMinutes?: number | null;
    tagIds?: string[];
  }) {
    const response = await this.client.post('/developer/games/submit', data);
    return response.data;
  }

  async updateDeveloperGame(gameId: string, data: {
    title?: string;
    description?: string;
    publisher?: string;
    version?: string;
    iconUrl?: string;
    bannerUrl?: string;
    galleryImages?: string[];
    accessibilityFeatureIds?: string[];
	    isDrmFree?: boolean;
	    ageRatings?: AgeRating[];
	    systemRequirements?: GameSystemRequirements;
    estimatedPlaytime?: string;
    estimatedPlaytimeMinutes?: number | null;
    tagIds?: string[];
  }) {
    const response = await this.client.post('/developer/games/update', { gameId, ...data });
    return response.data;
  }

  async submitForReview(gameId: string) {
    const response = await this.client.post('/developer/games/submit-for-review', { gameId });
    return response.data;
  }

  async getLatestAppDownloadUrl(platform: 'windows-x64' | 'mac-universal') {
    const response = await this.client.post('/app/update/download-url', { platform });
    return response.data as {
      success: boolean;
      message?: string;
      data: {
        downloadUrl: string;
        version: string;
        fileSize: number;
        checksum: string;
        fileName: string;
      };
    };
  }

  async getLatestManualAppDownloadUrl(platform: 'windows-x64' | 'mac-universal') {
    const response = await this.client.post('/app/update/manual-download-url', { platform });
    return response.data as {
      success: boolean;
      message?: string;
      data: {
        downloadUrl: string;
        version: string;
        fileSize: number;
        checksum: string;
        fileName: string;
      };
    };
  }

  // Multipart upload methods for large files
  async initiateMultipartUpload(params: {
    appId: number;
    version: string;
    platform: string;
    filename: string;
    fileSize: number;
    totalParts: number;
  }) {
    const response = await this.client.post('/developer/games/initiate-multipart-upload', params);
    return response.data as {
      success: boolean;
      data: {
        uploadId: string;
        key: string;
        bucket: string;
        path: string;
        totalParts: number;
      };
    };
  }

  async getPartUploadUrls(params: {
    uploadId: string;
    key: string;
    partNumbers: number[];
  }) {
    const response = await this.client.post('/developer/games/get-part-upload-urls', params);
    return response.data as {
      success: boolean;
      data: {
        urls: Array<{ partNumber: number; url: string }>;
      };
    };
  }

  async completeMultipartUpload(params: {
    uploadId: string;
    key: string;
    appId: number;
    version: string;
    platform: string;
    fileSize: number;
    parts: Array<{ partNumber: number; etag: string }>;
    checksum?: string;
    executablePath?: string;
  }) {
    const response = await this.client.post('/developer/games/complete-multipart-upload', params);
    return response.data as {
      success: boolean;
      data: {
        bucket: string;
        path: string;
        size: number;
        checksum?: string;
        version: string;
        platform: string;
        location: string;
      };
    };
  }

  async publishGameBuild(buildId: string, channel: string = 'public') {
    const response = await this.client.post('/developer/games/builds/publish', { buildId, channel });
    return response.data;
  }

  async unpublishGameBuild(buildId: string, channel: string = 'public') {
    const response = await this.client.post('/developer/games/builds/unpublish', { buildId, channel });
    return response.data;
  }

  async abortMultipartUpload(params: { uploadId: string; key: string }) {
    const response = await this.client.post('/developer/games/abort-multipart-upload', params);
    return response.data as { success: boolean; message: string };
  }

  async uploadGameMedia(params: { appId: number; type: 'icon' | 'banner' | 'gallery'; file: File }) {
    const form = new FormData();
    form.append('appId', String(params.appId));
    form.append('type', params.type);
    form.append('file', params.file);
    const response = await this.client.post('/developer/games/upload-media', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Achievement endpoints
  async getGameAchievements(gameId: string) {
    const response = await this.client.post('/achievements/game-achievements', { gameId });
    return response.data;
  }

  async createAchievement(data: {
    gameId: string;
    achievementId: string;
    name: string;
    description?: string;
    additionalDescription?: string;
    lockedIconUrl?: string;
    unlockedIconUrl?: string;
    metadata?: Record<string, any>;
  }) {
    const response = await this.client.post('/achievements/create', data);
    return response.data;
  }

  async updateAchievement(achievementId: string, data: {
    name?: string;
    description?: string;
    additionalDescription?: string;
    lockedIconUrl?: string;
    unlockedIconUrl?: string;
    metadata?: Record<string, any>;
  }) {
    const response = await this.client.post('/achievements/update', { achievementId, ...data });
    return response.data;
  }

  async deleteAchievement(achievementId: string) {
    const response = await this.client.post('/achievements/delete', { achievementId });
    return response.data;
  }

  async uploadAchievementIcon(params: { achievementId: string; iconType: 'locked' | 'unlocked'; file: File }) {
    const form = new FormData();
    form.append('achievementId', params.achievementId);
    form.append('iconType', params.iconType);
    form.append('file', params.file);
    const response = await this.client.post('/achievements/upload-icon', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  // Stats endpoints
  async getGameStats(gameId: string) {
    const response = await this.client.post('/stats/game-stats', { gameId });
    return response.data;
  }

  async createStat(data: {
    gameId: string;
    statId: string;
    name: string;
    type?: 'INT' | 'FLOAT' | 'AVGRATE';
    incrementOnly?: boolean;
    maxChange?: number;
    minValue?: number;
    maxValue?: number;
    windowSize?: number;
    defaultValue?: string;
    aggregated?: boolean;
    metadata?: Record<string, any>;
  }) {
    const response = await this.client.post('/stats/create', data);
    return response.data;
  }

  async updateStat(statId: string, data: {
    name?: string;
    type?: 'INT' | 'FLOAT' | 'AVGRATE';
    incrementOnly?: boolean;
    maxChange?: number | null;
    minValue?: number | null;
    maxValue?: number | null;
    windowSize?: number | null;
    defaultValue?: string;
    aggregated?: boolean;
    metadata?: Record<string, any>;
  }) {
    const response = await this.client.post('/stats/update', { statId, ...data });
    return response.data;
  }

  async deleteStat(statId: string) {
    const response = await this.client.post('/stats/delete', { statId });
    return response.data;
  }
}

export const apiClient = new ApiClient();

function createTagSlug(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export const authApiAdapter: AuthApiClient = {
  logout: () => apiClient.logout(),
  getCurrentUser: () => apiClient.getCurrentUser(),
  validateToken: () => apiClient.validateToken(),
  sendOtp: (email: string) => apiClient.sendOtp(email),
  verifyOtp: (email: string, token: string, firstName?: string, lastName?: string, orgUsername?: string) =>
    apiClient.verifyOtp(email, token, { firstName, lastName, orgUsername }),
};

